import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { noop } from "lodash";

import { renderDefaultTextComponent } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { QueryBarItem } from "app/components/compare/QueryBarItem";
import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { getQueryBarSecondaryTextMap } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { LoadingComponent } from "app/components/compare/WebsiteQueryBar/WebsiteQueryBar";
import { CHART_COLORS } from "app/constants/ChartColors";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { translate } from "app/filters/i18nFilter";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";
import { FlexRow, LeftFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";

import { QueryBarCompareButton } from "./QueryBarCompareButton";
import { QueryBarWebsiteItemStyled, VS } from "./StyledComponents";
import type { WebsiteItem, WebsitePageResults } from "./types";
import {
    getPrimarySite,
    getUpdatedCompetitorsOnAddCompetitor,
    trackAutocompleteOpen,
    trackAutocompleteSelectSite,
    updateUrlWithCompetitors,
} from "./utils";

const colors = CHART_COLORS.chartMainColors;

export const InlineWebsiteQueryBar: FC = () => {
    const { pageFilter, competitorsFilter } = useCurrentStateAndParams().params;
    const primarySiteItem = getPrimarySite(pageFilter as string);
    const similarSites = chosenSites.similarSitesData() as ISimilarInfoItem[];
    const [competitors, setCompetitors] = useState<WebsitePageResults>([]);
    const [selectedCompetitor, setSelectedCompetitor] = useState<string>(null);

    const clearSelectedCompetitor = () => setSelectedCompetitor(null);

    const competitorsParamItems = useMemo(() => {
        return safeJsonParse(competitorsFilter as string, []) as WebsiteItem[];
    }, [competitorsFilter]);

    const domainList = useMemo(() => {
        if (!competitorsParamItems.length) {
            return [];
        }
        return competitorsParamItems.map((page) => page.url.split("/")[0]);
    }, [competitorsParamItems]);

    const { data: domainFavicons = {}, isLoading } = useWebsitesFavicons(domainList, undefined, {
        enabled: domainList.length > 0,
    });

    useEffect(() => {
        if (!competitorsParamItems.length || !domainList.length) {
            return;
        }

        // Wait for favicons to load
        if (isLoading || Object.keys(domainFavicons).length === 0) {
            return;
        }

        const competitorsData = domainList.map((domain, index) => {
            const { url, searchType } = competitorsParamItems.find((item) => item.url.startsWith(domain));
            const competitorIcon = domainFavicons[domain];
            return {
                url,
                searchType,
                name: url,
                icon: competitorIcon,
                color: CHART_COLORS.compareMainColors[index + 1],
            };
        });
        setCompetitors(competitorsData);
    }, [competitorsParamItems, domainList, domainFavicons, isLoading]);

    const onRemoveCompetitor = (domain: string) => () => {
        const updatedCompetitors = competitors.filter((competitor) => competitor.name !== domain);
        setCompetitors(updatedCompetitors);
        updateUrlWithCompetitors(updatedCompetitors);
    };

    const onAddCompetitor = (competitorToAdd: IWebsitePageResult) => {
        setCompetitors((currentCompetitors) => {
            const activeCompetitorIndex = currentCompetitors.findIndex((comp) => comp.name === selectedCompetitor);
            if (activeCompetitorIndex === -1) {
                updateUrlWithCompetitors(currentCompetitors);
                return currentCompetitors;
            } else {
                trackAutocompleteSelectSite(competitorToAdd.name);
                const updatedCompetitors = getUpdatedCompetitorsOnAddCompetitor(
                    currentCompetitors,
                    competitorToAdd,
                    activeCompetitorIndex,
                );
                updateUrlWithCompetitors(updatedCompetitors);
                return updatedCompetitors;
            }
        });
        clearSelectedCompetitor();
    };

    const sitesToExcludeFromAutocomplete = [
        { ...primarySiteItem, name: primarySiteItem.url } as IChosenItem,
        ...(competitors?.map((c) => ({ ...c, url: c.name }) as IChosenItem) ?? []),
    ].filter(Boolean);

    return (
        <LeftFlexRow data-automation="inline-website-query-bar">
            <QueryBarWebsiteItemStyled
                text={primarySiteItem.url}
                image={primarySiteItem.icon}
                badgeColor={colors[0]}
                isCompare={true}
                hideRemoveButton={true}
                onItemClick={noop}
                isDisabled={true}
                secondaryText={getQueryBarSecondaryTextMap()[primarySiteItem?.searchType ?? "domain"]}
            />
            {competitors?.length ? (
                <>
                    <VS>{translate("common.vs")}</VS>
                    {competitors?.map(({ name, icon, searchType, secondaryText }, index) => (
                        <div key={name}>
                            <QueryBarItem
                                id={name}
                                isLoading={isLoading}
                                activeItem={selectedCompetitor}
                                renderComponent={
                                    <FlexRow>
                                        <QueryBarWebsiteItemStyled
                                            $isLast={!!(competitors.length > 1 && index === competitors.length - 1)}
                                            text={name}
                                            image={icon}
                                            onItemClick={() => setSelectedCompetitor(name)}
                                            onButtonClick={onRemoveCompetitor(name)}
                                            badgeColor={colors[index + 1]}
                                            isCompare={true}
                                            secondaryText={secondaryText ?? getQueryBarSecondaryTextMap()[searchType]}
                                            textRenderFunction={(...args) => {
                                                const isPageLevel = searchType === "exact" || searchType === "folder";
                                                return renderDefaultTextComponent(
                                                    args[0],
                                                    isPageLevel ? args[1] : args[1]?.split("/")[0],
                                                    args[2],
                                                    isPageLevel,
                                                );
                                            }}
                                        />
                                    </FlexRow>
                                }
                                searchComponent={
                                    <ClickOutsideWrapper onClickOutside={clearSelectedCompetitor}>
                                        <AutocompleteWebsitePagesCompareItem
                                            className="AutocompleteInlineWebsiteQueryBar"
                                            secondColumnName="secondaryText"
                                            onClick={onAddCompetitor}
                                            similarSites={similarSites}
                                            defaultValue={
                                                searchType !== "domain" && searchType !== "subdomain" ? name : undefined
                                            }
                                            excludes={sitesToExcludeFromAutocomplete}
                                            onCloseButtonClick={clearSelectedCompetitor}
                                            overrideEnterFunc={(_, item) => {
                                                if (item) {
                                                    onAddCompetitor(item as IWebsitePageResult);
                                                }
                                                clearSelectedCompetitor();
                                            }}
                                            autocompleteProps={{
                                                placeholder: `${selectedCompetitor ? selectedCompetitor : ""}`,
                                                width: 400,
                                                onFocus: () => {
                                                    trackAutocompleteOpen();
                                                },
                                            }}
                                        />
                                    </ClickOutsideWrapper>
                                }
                                loadingComponent={
                                    <LoadingComponent
                                        placeholderText={name}
                                        headerText={translate(
                                            "autocomplete.websitePagesCompare.seperator.text.similarSites",
                                        )}
                                    />
                                }
                            />
                        </div>
                    ))}
                </>
            ) : null}
            <QueryBarCompareButton
                competitorsCount={competitors?.length}
                updateCompetitors={setCompetitors}
                similarSites={similarSites}
                sitesToExclude={sitesToExcludeFromAutocomplete}
            />
        </LeftFlexRow>
    );
};
